import { createAsyncThunk } from '@reduxjs/toolkit';

import { TAB_DEFAULT_BASE_NAME } from '@/features/Dashboard/constants/names';
import type {
  DashboardTab,
  MarketApps,
  MarketAppWithTabs,
  TileBigNumber,
  WidgetV2
} from '@/features/Dashboard/types';
import { getUniqueDashboardName } from '@/features/Dashboard/utils/getUniqueDashboardName';
import type { RootState } from '@/store';

import {
  createDashboardDraft,
  ensureTabExistsInOtherDashboard as ensureTabAction,
  initializeDraft,
  initializeOtherDashboardDraft,
  moveAppTabToOtherDashboard as moveAppTabAction,
  moveAppToOtherDashboard as moveAppAction,
  moveTabToOtherDashboard as moveTabAction,
  moveTileToOtherDashboard as moveTileAction,
  resetDraft,
  setAllDashboardsData
} from './dashboardEdit.slice';
import {
  getTabValidationErrorMessage,
  getTileValidationErrorMessage,
  isCurrentDashboard,
  validateTabMove,
  validateTileAddition
} from './utils/helpers';
import { mapServerDataToDraft } from './utils/mapping';

interface InitializePayload {
  dashboardName: string;
  initialDashboardName: string;
  dashboardConfig: MarketApps;
  currentDashboardTabs: DashboardTab;
  tabNames: string[];
  activeTab: number;
}

export const initializeDashboardEditThunk = createAsyncThunk(
  'dashboardEdit/initialize',
  (payload: InitializePayload, { dispatch }) => {
    const { dashboardName, initialDashboardName, currentDashboardTabs } = payload;

    const { draftTabs, tabDrafts } = mapServerDataToDraft(currentDashboardTabs);

    dispatch(
      initializeDraft({
        dashboardName,
        initialDashboardName,
        draftTabs,
        tabDrafts
      })
    );
  }
);

/**
 * Create a new dashboard draft with a unique name based on current state.
 */
export const createUniqueDashboardDraftThunk = createAsyncThunk<
  { dashboardName: string },
  void,
  { state: RootState }
>('dashboardEdit/createUniqueDashboardDraft', (_, { getState, dispatch }) => {
  const state = getState().dashboardEdit;
  const existingNames = new Set(Object.keys(state.allDashboardsData));

  Object.keys(state.otherDashboardDrafts).forEach(name => existingNames.add(name));

  if (state.dashboardName) {
    existingNames.add(state.dashboardName);
  }

  if (state.initialDashboardName) {
    existingNames.add(state.initialDashboardName);
  }

  const dashboardName = getUniqueDashboardName(Array.from(existingNames));
  dispatch(createDashboardDraft({ name: dashboardName }));
  return { dashboardName };
});

interface MoveTilePayload {
  targetDashboardName: string;
  targetTabId: string;
  tile: TileBigNumber;
}

/**
 * Thunk for moving a tile to another dashboard with validation.
 * Validates:
 * - Maximum 8 tiles per tab
 * - No duplicate tile codes in the same tab
 *
 * If target dashboard doesn't have a draft, automatically initializes it
 * from allDashboardsData to preserve existing tabs.
 *
 * Returns error message on validation failure, undefined on success.
 */
export const moveTileToOtherDashboardThunk = createAsyncThunk<
  void,
  MoveTilePayload,
  { state: RootState; rejectValue: string }
>('dashboardEdit/moveTileToOtherDashboard', (payload, { getState, dispatch, rejectWithValue }) => {
  const { targetDashboardName, targetTabId, tile } = payload;
  let activeState = getState().dashboardEdit;

  // Initialize target dashboard draft from server data if needed
  if (
    !isCurrentDashboard(activeState, targetDashboardName) &&
    !activeState.otherDashboardDrafts[targetDashboardName]
  ) {
    const serverData = activeState.allDashboardsData[targetDashboardName];
    if (serverData) {
      dispatch(
        initializeOtherDashboardDraft({
          dashboardName: targetDashboardName,
          serverData
        })
      );
      activeState = getState().dashboardEdit;
    }
  }

  // Get or create the target tab draft for validation
  let targetTabDraft;

  if (isCurrentDashboard(activeState, targetDashboardName)) {
    targetTabDraft = activeState.tabDrafts[targetTabId];
    if (!targetTabDraft) {
      return rejectWithValue('Target tab not found');
    }
  } else {
    // For other dashboards, we need to check or simulate the draft
    const dashboardDraft = activeState.otherDashboardDrafts[targetDashboardName] || {
      draftTabs: [],
      tabDrafts: {}
    };
    targetTabDraft = dashboardDraft.tabDrafts[targetTabId] || { marketTabApps: [], tiles: [] };
  }

  // Validate tile addition
  const validationError = validateTileAddition(targetTabDraft, tile);

  if (validationError) {
    return rejectWithValue(getTileValidationErrorMessage(validationError));
  }

  // Validation passed, dispatch the action
  dispatch(moveTileAction({ targetDashboardName, targetTabId, tile }));
});

interface MoveTabPayload {
  targetDashboardName: string;
  tabId: string;
  tabName: string;
}

/**
 * Thunk for moving a tab to another dashboard with validation.
 * Validates:
 * - No duplicate tab names in the target dashboard (case-insensitive)
 *
 * Pre-initializes the target dashboard draft from server data if needed,
 * so the reducer never has to read from Immer proxy state.
 *
 * Returns error message on validation failure, undefined on success.
 */
export const moveTabToOtherDashboardThunk = createAsyncThunk<
  void,
  MoveTabPayload,
  { state: RootState; rejectValue: string }
>('dashboardEdit/moveTabToOtherDashboard', (payload, { getState, dispatch, rejectWithValue }) => {
  const { targetDashboardName, tabId, tabName } = payload;
  let state = getState().dashboardEdit;

  // Don't move to the same dashboard (no-op)
  if (isCurrentDashboard(state, targetDashboardName)) {
    return;
  }

  // Pre-initialize target dashboard draft from server data if needed
  if (!state.otherDashboardDrafts[targetDashboardName]) {
    const serverData = state.allDashboardsData[targetDashboardName];
    if (serverData) {
      dispatch(initializeOtherDashboardDraft({ dashboardName: targetDashboardName, serverData }));
      state = getState().dashboardEdit;
    }
  }

  // Get target dashboard tabs for validation
  const targetDashboardDraft = state.otherDashboardDrafts[targetDashboardName];
  const targetTabs = targetDashboardDraft ? targetDashboardDraft.draftTabs : [];

  // Use default name if current name is empty
  const effectiveTabName = tabName.trim() || TAB_DEFAULT_BASE_NAME;

  // Validate tab name
  const validationError = validateTabMove(targetTabs, effectiveTabName);

  if (validationError) {
    return rejectWithValue(getTabValidationErrorMessage(validationError, effectiveTabName));
  }

  // Read source content from plain state (not Immer proxy) and pass via payload
  const sourceContent = state.tabDrafts[tabId];
  if (!sourceContent) return;

  dispatch(
    moveTabAction({
      targetDashboardName,
      tabId,
      tabName: effectiveTabName,
      marketTabApps: sourceContent.marketTabApps,
      tiles: sourceContent.tiles
    })
  );
});

interface MoveAppPayload {
  targetDashboardName: string;
  targetTabId: string;
  app: MarketAppWithTabs;
}

/**
 * Thunk for moving an app to another dashboard.
 * Pre-initializes the target dashboard draft from server data if needed.
 */
export const moveAppToOtherDashboardThunk = createAsyncThunk<
  void,
  MoveAppPayload,
  { state: RootState }
>('dashboardEdit/moveAppToOtherDashboardThunk', (payload, { getState, dispatch }) => {
  const { targetDashboardName } = payload;
  const state = getState().dashboardEdit;

  if (
    !isCurrentDashboard(state, targetDashboardName) &&
    !state.otherDashboardDrafts[targetDashboardName]
  ) {
    const serverData = state.allDashboardsData[targetDashboardName];
    if (serverData) {
      dispatch(initializeOtherDashboardDraft({ dashboardName: targetDashboardName, serverData }));
    }
  }

  dispatch(moveAppAction(payload));
});

interface MoveAppTabPayload {
  targetDashboardName: string;
  targetTabId: string;
  tab: WidgetV2;
}

/**
 * Thunk for moving an app tab to another dashboard.
 * Pre-initializes the target dashboard draft from server data if needed.
 */
export const moveAppTabToOtherDashboardThunk = createAsyncThunk<
  void,
  MoveAppTabPayload,
  { state: RootState }
>('dashboardEdit/moveAppTabToOtherDashboardThunk', (payload, { getState, dispatch }) => {
  const { targetDashboardName } = payload;
  const state = getState().dashboardEdit;

  if (
    !isCurrentDashboard(state, targetDashboardName) &&
    !state.otherDashboardDrafts[targetDashboardName]
  ) {
    const serverData = state.allDashboardsData[targetDashboardName];
    if (serverData) {
      dispatch(initializeOtherDashboardDraft({ dashboardName: targetDashboardName, serverData }));
    }
  }

  dispatch(moveAppTabAction(payload));
});

interface EnsureTabPayload {
  dashboardName: string;
  tabId: string;
  tabName: string;
}

/**
 * Ensures a tab exists in another dashboard's draft.
 * Pre-initializes the dashboard draft from server data if needed.
 */
export const ensureTabExistsInOtherDashboardThunk = createAsyncThunk<
  void,
  EnsureTabPayload,
  { state: RootState }
>('dashboardEdit/ensureTabExistsInOtherDashboard', (payload, { getState, dispatch }) => {
  const { dashboardName } = payload;
  const state = getState().dashboardEdit;

  if (!isCurrentDashboard(state, dashboardName) && !state.otherDashboardDrafts[dashboardName]) {
    const serverData = state.allDashboardsData[dashboardName];
    if (serverData) {
      dispatch(initializeOtherDashboardDraft({ dashboardName, serverData }));
    }
  }

  dispatch(ensureTabAction(payload));
});

interface ReinitializePayload {
  dashboardName: string;
  activeTab?: number;
  allDashboardsData?: Record<string, DashboardTab>;
}

/**
 * Reinitialize draft when switching dashboards.
 * If allDashboardsData is provided, updates the cache first.
 * Otherwise, uses existing cached data to create the draft.
 *
 * This thunk handles the case where:
 * 1. The user switches dashboards while in edit mode
 * 2. The draft doesn't exist for the new dashboard
 * 3. But the dashboard data exists in allDashboardsData
 */
export const reinitializeDraftThunk = createAsyncThunk<
  void,
  ReinitializePayload,
  { state: RootState; rejectValue: string }
>('dashboardEdit/reinitialize', (payload, { getState, dispatch, rejectWithValue }) => {
  const { dashboardName, allDashboardsData: newDashboardsData } = payload;

  // Update cache if new data provided
  if (newDashboardsData) {
    dispatch(setAllDashboardsData({ data: newDashboardsData }));
  }

  // Get current state (may have been updated by setAllDashboardsData)
  const state = getState().dashboardEdit;
  const serverData = newDashboardsData?.[dashboardName] ?? state.allDashboardsData[dashboardName];

  if (!serverData) {
    return rejectWithValue(`Dashboard "${dashboardName}" not found in cached data`);
  }

  const { draftTabs, tabDrafts } = mapServerDataToDraft(serverData);

  // Reset current draft and initialize new one
  dispatch(resetDraft());
  dispatch(
    initializeDraft({
      dashboardName,
      initialDashboardName: dashboardName,
      draftTabs,
      tabDrafts
    })
  );
});
